// =============================================================================
// SIREPPAR - Consultas a la SEP: nueva consulta (#388)
// =============================================================================
// Si llegan expedienteId y observacionId por la query string (desde una
// intimación u observación del expediente, #389), quedan fijos como referencia.
// Una consulta NO es una respuesta: no cambia la observación ni corta plazos.
// =============================================================================

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import RichTextEditor from '../../components/common/RichTextEditor';
import AdjuntosInforme, { MAX_ADJUNTOS, MAX_MB_ADJUNTO } from '../../components/common/AdjuntosInforme';
import { consultaService, expedienteService } from '../../services/api';
import { usePuedeCrearConsulta } from '../../hooks/usePuedeCrearConsulta';
import { tipoTramiteLabel } from '../../utils/tiposExpediente';
import ReferenciaObservacionConsulta, { AvisoPlazoIntimacion, ObservacionReferencia } from '../../components/common/ReferenciaObservacionConsulta';
import { ASUNTO_MAX, ASUNTO_MIN, TEXTO_MAX, mensajeDeError } from '../../utils/consultas';

interface ExpedienteOpcion {
  id: string;
  numero: string;
  tipo: string;
}

interface Referencia {
  expediente: { id: string; numero: string };
  observacion: ObservacionReferencia;
  intimacionVigente: boolean;
}

export default function ConsultaNuevaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { puedeCrear, cargando: cargandoPermiso } = usePuedeCrearConsulta();
  const [searchParams] = useSearchParams();
  const expedienteFijo = searchParams.get('expedienteId') ?? '';
  const observacionFija = searchParams.get('observacionId') ?? '';

  const [asunto, setAsunto] = useState('');
  const [texto, setTexto] = useState('');
  const [expedienteId, setExpedienteId] = useState(expedienteFijo);
  const [archivos, setArchivos] = useState<File[]>([]);

  const { data: expedientes = [] } = useQuery<ExpedienteOpcion[]>({
    queryKey: ['consultas-expedientes-agrupacion'],
    queryFn: async () => {
      const res = await expedienteService.list({ pageSize: 100 });
      return res.data?.data?.items ?? res.data?.data ?? [];
    },
  });

  // #389: consulta iniciada desde una observación o intimación del expediente.
  const conReferencia = !!expedienteFijo && !!observacionFija;
  const { data: referencia, isError: errorReferencia } = useQuery<Referencia>({
    queryKey: ['consultas-referencia', expedienteFijo, observacionFija],
    queryFn: async () => (await consultaService.getReferencia(expedienteFijo, observacionFija)).data,
    enabled: conReferencia,
    retry: false,
  });

  // Asunto inicial, editable, en cuanto llega la referencia.
  useEffect(() => {
    if (!referencia) return;
    const tipo = referencia.observacion.tipo === 'INTIMACION' ? 'la intimación' : 'la observación';
    setAsunto((actual) => actual || `Consulta sobre ${tipo} del expediente ${referencia.expediente.numero}`);
  }, [referencia]);

  const enviar = useMutation({
    mutationFn: () => {
      const datos = new FormData();
      datos.append('asunto', asunto.trim());
      datos.append('texto', texto.trim());
      if (expedienteId) datos.append('expedienteId', expedienteId);
      if (observacionFija) datos.append('observacionId', observacionFija);
      archivos.forEach((archivo) => datos.append('archivos', archivo));
      return consultaService.create(datos);
    },
    onSuccess: (res) => {
      toast.success('Consulta enviada');
      queryClient.invalidateQueries({ queryKey: ['consultas'] });
      navigate(res?.data?.id ? `/consultas/${res.data.id}` : '/consultas');
    },
    onError: (e) => toast.error(mensajeDeError(e, 'No se pudo enviar la consulta')),
  });

  const asuntoLimpio = asunto.trim();
  const textoLimpio = texto.trim();
  const errorAsunto = asuntoLimpio.length > 0 && asuntoLimpio.length < ASUNTO_MIN
    ? `El asunto tiene que tener al menos ${ASUNTO_MIN} caracteres.`
    : asuntoLimpio.length > ASUNTO_MAX ? `El asunto puede tener hasta ${ASUNTO_MAX} caracteres.` : '';
  const errorTexto = textoLimpio.length > TEXTO_MAX ? `La consulta puede tener hasta ${TEXTO_MAX} caracteres.` : '';
  const puedeEnviar = asuntoLimpio.length >= ASUNTO_MIN && !errorAsunto && textoLimpio.length > 0 && !errorTexto
    && !enviar.isPending && (!conReferencia || !!referencia);

  const expedienteElegido = expedientes.find((e) => e.id === expedienteId);

  if (cargandoPermiso) return <p className="text-sm text-gray-500">Cargando...</p>;

  if (!puedeCrear) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-lg border border-gray-200 p-6">
        <h1 className="text-lg font-medium text-gray-900">Nueva consulta</h1>
        <p className="text-sm text-gray-600 mt-2">
          Solo los apoderados firmantes y los apoderados iniciadores de la agrupación pueden iniciar consultas.
          Puede ver y responder las consultas de su agrupación desde la bandeja.
        </p>
        <button
          type="button"
          onClick={() => navigate('/consultas')}
          className="mt-4 px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Ir a las consultas
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Nueva consulta</h1>
        <p className="text-sm text-gray-600 mt-2">
          Una consulta no reemplaza la respuesta a una observación o intimación. Las respuestas se presentan desde el expediente.
        </p>
      </div>

      <form
        className="bg-white rounded-lg border border-gray-200 p-6 space-y-5"
        onSubmit={(e) => { e.preventDefault(); if (puedeEnviar) enviar.mutate(); }}
      >
        <div>
          <label htmlFor="asunto" className="block text-sm font-medium text-gray-700 mb-1">Asunto</label>
          <input
            id="asunto"
            value={asunto}
            onChange={(e) => setAsunto(e.target.value)}
            maxLength={ASUNTO_MAX}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Tema de la consulta"
          />
          {errorAsunto && <p className="text-xs text-red-600 mt-1">{errorAsunto}</p>}
        </div>

        {conReferencia && (
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">Referencia</span>
            {referencia ? (
              <ReferenciaObservacionConsulta observacion={referencia.observacion} expediente={referencia.expediente} />
            ) : errorReferencia ? (
              <p className="text-sm text-red-600">No se pudo cargar la observación de referencia. Vuelva al expediente e intente de nuevo.</p>
            ) : (
              <p className="text-sm text-gray-500">Cargando la referencia...</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="expediente" className="block text-sm font-medium text-gray-700 mb-1">Expediente (opcional)</label>
          {expedienteFijo ? (
            <p className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              {expedienteElegido
                ? `${expedienteElegido.numero} — ${tipoTramiteLabel(expedienteElegido.tipo)}`
                : referencia?.expediente.numero ?? 'Expediente de referencia'}
            </p>
          ) : (
            <select
              id="expediente"
              value={expedienteId}
              onChange={(e) => setExpedienteId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Sin expediente</option>
              {expedientes.map((e) => (
                <option key={e.id} value={e.id}>{e.numero} — {tipoTramiteLabel(e.tipo)}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Consulta</span>
          <RichTextEditor value={texto} onChange={setTexto} placeholder="Escriba su consulta" minHeight={180} />
          {errorTexto && <p className="text-xs text-red-600 mt-1">{errorTexto}</p>}
        </div>

        <AdjuntosInforme
          archivos={archivos}
          onChange={setArchivos}
          ayuda={`Hasta ${MAX_ADJUNTOS} archivos PDF o imagen, de ${MAX_MB_ADJUNTO} MB cada uno.`}
        />

        {referencia?.intimacionVigente && <AvisoPlazoIntimacion />}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/consultas')}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!puedeEnviar}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {enviar.isPending ? 'Enviando...' : 'Enviar consulta'}
          </button>
        </div>
      </form>
    </div>
  );
}
